/**
 * @file StatementsPanel.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-04
 * @modified 2026-10-01
 *
 * Contact-modal panel: a customer's statement history — each statement run, its
 * status, and the full delivery log (what was sent, when, how, by whom, and the
 * SMTP response). Per-row Preview and Regenerate/Redeliver via StatementDeliverModal.
 *
 * A failed or bounced email that was followed by a later send shows the resent
 * mark (StatementDeliveryLog's ResentFlag) instead of its failure flag; the
 * rule lives in utils/statementDelivery.js, shared with the run history.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import StatementDeliverModal from './StatementDeliverModal';
import './StatementsPanel.css';
import EntityLink from './EntityLink';
import StatementDeliveryLog, { DELIVERY_ACTION_META as ACTION_META, ResentFlag } from './StatementDeliveryLog';
import { deliveryOutcome, badgeFailed } from '../utils/statementDelivery';

function StatementsPanel({ contactId, contact }) {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [deliverFor, setDeliverFor] = useState(null);

  // A statement-linked child rolls up into its parent's joint statement and
  // has no statements of its own — so show the parent's history instead.
  const jointChild = !!(contact?.parentAccount && contact?.link_statements === true);
  const parent = jointChild ? contact.parentAccount : null;
  const effectiveId = jointChild ? parent.id : contactId;

  const fetchStatements = useCallback(async () => {
    if (!effectiveId) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/statements/by-account/${effectiveId}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load statements');
      setStatements(data.statements || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [effectiveId]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const withAccount = (s) => ({
    ...s,
    account_no: jointChild ? parent.account_no : contact?.account_no,
    company_name: jointChild ? parent.company_name : contact?.company_name,
    customer_email: jointChild ? undefined : contact?.email
  });

  const periodLabel = (s) => {
    if (!s.period_end) return '—';
    const d = new Date(s.period_end);
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="statements-panel">
      {jointChild && (
        <div className="sp-joint-notice">
          <span className="material-symbols-rounded">info</span>
          <span>
            Statements for this account are issued jointly under{' '}
            <strong>
              <EntityLink type="contact" id={parent.id}>{parent.company_name}</EntityLink>
            </strong>{parent.account_no ? ` (${parent.account_no})` : ''}.
            Showing the parent’s statement history — this account is included.
          </span>
        </div>
      )}
      {loading && <div className="sp-info">Loading…</div>}
      {error && <div className="sp-info sp-error">{error}</div>}
      {!loading && !error && statements.length === 0 && (
        <div className="sp-info">No statements {jointChild ? `for ${parent.company_name}` : 'for this contact'} yet.</div>
      )}

      {statements.length > 0 && (
        <table className="data-table sp-table">
          <thead>
            <tr>
              <th style={{width: "80px"}}>Period</th>
              <th style={{width: "110px", textAlign: "center"}}>Status</th>
              <th>Delivered</th>
              <th className="num">Balance</th>
              <th className="actions-col" style={{width: "110px"}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => {
              const isOpen = expanded.has(s.id);
              const lastSent = s.deliveries.filter(d => d.status === 'sent').slice(-1)[0];
              const outcome = deliveryOutcome(s.deliveries, s);
              return (
                <React.Fragment key={s.id}>
                  <tr className={isOpen ? 'sp-row-open' : ''}>
                    <td>{periodLabel(s)}</td>
                    <td style={{textAlign: "center"}}>
                      <span className={`sp-status sp-${s.status}`}>{s.status}</span>
                      {outcome.failed && <span className="sp-status sp-failed" title="A delivery failed">!</span>}
                      {outcome.bounced &&
                        <span className="sp-status sp-bounced" title="A message bounced or was marked as spam">⚠</span>}
                      <ResentFlag outcome={outcome} />
                    </td>
                    <td>
                      <div className="sp-deliv-badges">
                        {s.deliveries.length === 0 && <span className="sp-muted">—</span>}
                        {[...new Set(s.deliveries.map(d => d.recipient_type))].filter(Boolean).map(rt => {
                          const m = ACTION_META[rt] || { icon: 'help', label: rt };
                          const failed = badgeFailed(outcome, rt);
                          return (
                            <span key={rt} className={`sp-badge ${failed ? 'sp-badge-failed' : ''}`} title={m.label}>
                              <span className="material-symbols-rounded">{m.icon}</span>
                            </span>
                          );
                        })}
                        {lastSent && <span className="sp-when">{lastSent.ts}</span>}
                      </div>
                    </td>
                    <td className="num">{s.total_due != null ? `£${Number(s.total_due).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn-icon" title="Delivery log" onClick={() => toggle(s.id)}>
                          <span className="material-symbols-rounded">{isOpen ? 'expand_less' : 'history'}</span>
                        </button>
                        <button className="btn-icon" title="Regenerate / redeliver" onClick={() => setDeliverFor(withAccount(s))}>
                          <span className="material-symbols-rounded">send</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="sp-detail-row">
                      <td colSpan={5}>
                        <StatementDeliveryLog deliveries={s.deliveries} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {deliverFor && (
        <StatementDeliverModal
          statement={deliverFor}
          onClose={() => setDeliverFor(null)}
          onDelivered={fetchStatements}
        />
      )}
    </div>
  );
}

export default StatementsPanel;
